// src/components/canine-teeth/SubImageAnnotator.js

import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { debounce } from 'lodash';
import { saveSession, getSessionByImageNameAndEmail } from '../../indexedDB';
import { flushSync } from 'react-dom';
import PropTypes from 'prop-types';

import {
  FaDownload,
  FaUndo,
  FaSave,
  FaMinus,
  FaEraser,
  FaPaintBrush,
  FaCircle,
  FaSearchMinus,
  FaCog,
  FaAdjust,
  FaSun,
} from 'react-icons/fa';
import { saveAs } from 'file-saver';
import html2canvas from 'html2canvas';
import '../tools.css';

// Aggiungi 'toothType' nelle props
function SubImageAnnotator({
  subImage,
  subImageId,
  toothType, // Nuova prop
  collectAnnotations,
  email,
  tipo,
  posizione,
  sesso,
  realAge = '', // Add realAge prop with default empty string
  selectableColors = ['blue', 'red', 'green'],
  initialAnnotations = null,
  updateSessionsCount,
  onExcludeChange,
  onRatiosChange,
  isExcluded,
  userInfo,
}) {
  const hasPanned = useRef(false);

  // Introduzione del flag per tracciare il caricamento iniziale
  const annotationsLoaded = useRef(false);
  // eslint-disable-next-line
  const saveCurrentSessionDebounced = useCallback(
    debounce(async (sessionData) => {
      console.log('Salvataggio sessione:', sessionData); // Log per debug
      await saveSession(sessionData);
      if (updateSessionsCount) {
        updateSessionsCount();
      }
    }, 1000),
    [updateSessionsCount]
  );



  const [mouseDownPosition, setMouseDownPosition] = useState({ x: null, y: null });
  const panningThreshold = 5;

  const tool = 'european-formula';
  const memoizedSelectableColors = useMemo(() => selectableColors, [selectableColors]);

  const colorMap = useMemo(() => {
    const map = {};
    memoizedSelectableColors.forEach((color, index) => {
      map[color] = (index + 1).toString();
    });
    return map;
  }, [memoizedSelectableColors]);

  const lineKeys = useMemo(
    () => memoizedSelectableColors.map((color) => `line${colorMap[color]}`),
    [memoizedSelectableColors, colorMap]
  );

  const [coordinates, setCoordinates] = useState(() =>
    lineKeys.reduce((acc, key) => ({ ...acc, [key]: [] }), {})
  );

  const [ratios, setRatios] = useState({});
  const [i3mValue, setI3MValue] = useState(null);
  const [currentLine, setCurrentLine] = useState(lineKeys[0]);
  const [lineThickness, setLineThickness] = useState(2);
  const [pointRadius, setPointRadius] = useState(5);
  const [showSettingsMenu, setShowSettingsMenu] = useState(false);
  const [showSaveMenu, setShowSaveMenu] = useState(false);
  const [activeSetting, setActiveSetting] = useState('');
  const [contrast, setContrast] = useState(100);
  const [brightness, setBrightness] = useState(100);
  const clickDisabled = useRef(false);
  const canvasRef = useRef(null);
  const imageRef = useRef(null);
  const imageContainerRef = useRef(null);

  const [zoomLevel, setZoomLevel] = useState(1);
  const [panOffset, setPanOffset] = useState({ x: 0, y: 0 });
  const minZoom = 0.5;
  const maxZoom = 5;


  const [isPanning, setIsPanning] = useState(false);
  const [panStart, setPanStart] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [draggingPoint, setDraggingPoint] = useState(null);
  const [overallRatio, setOverallRatio] = useState(0);


  const lineKeyToColor = useMemo(() => {
    const map = {};
    memoizedSelectableColors.forEach((color) => {
      const lineNumber = colorMap[color];
      map[`line${lineNumber}`] = color;
    });
    return map;
  }, [memoizedSelectableColors, colorMap]);

  const getLineColor = useCallback((lineKey) => {
    return lineKeyToColor[lineKey] || '#f0f0f0';
  }, [lineKeyToColor]);

  const handleLineChange = useCallback((newLine) => {
    setCurrentLine(newLine);
  }, []);

  const handleMouseMove = useCallback(
    (e) => {
      if (isDragging && draggingPoint) {
        const containerRect = imageContainerRef.current.getBoundingClientRect();
        const x = e.clientX - containerRect.left;
        const y = e.clientY - containerRect.top;

        const img = imageRef.current;
        const scaleX = img.naturalWidth / img.clientWidth;
        const scaleY = img.naturalHeight / img.clientHeight;

        const imageX = (x - panOffset.x) * scaleX / zoomLevel;
        const imageY = (y - panOffset.y) * scaleY / zoomLevel;

        setCoordinates((prevCoordinates) => {
          const updatedCoordinates = { ...prevCoordinates };
          updatedCoordinates[draggingPoint.key][draggingPoint.pointIndex] = {
            x: imageX,
            y: imageY,
          };
          return updatedCoordinates;
        });
        clickDisabled.current = true;

        if (canvasRef.current) {
          canvasRef.current.style.cursor = 'move';
        }
      } else if (isPanning) {
        e.preventDefault();

        const newPanOffset = {
          x: e.clientX - panStart.x,
          y: e.clientY - panStart.y,
        };
        setPanOffset(newPanOffset);
        hasPanned.current = true;

        if (canvasRef.current) {
          canvasRef.current.style.cursor = 'grabbing';
        }
      } else if (mouseDownPosition.x !== null && mouseDownPosition.y !== null) {
        const deltaX = e.clientX - mouseDownPosition.x;
        const deltaY = e.clientY - mouseDownPosition.y;
        const distance = Math.sqrt(deltaX * deltaX + deltaY * deltaY);

        if (distance > panningThreshold && zoomLevel > 1) {
          setIsPanning(true);
          setPanStart({
            x: mouseDownPosition.x - panOffset.x,
            y: mouseDownPosition.y - panOffset.y,
          });
          clickDisabled.current = true;
        } else {
          if (canvasRef.current) {
            canvasRef.current.style.cursor = 'crosshair';
          }
        }
      } else {
        const containerRect = imageContainerRef.current.getBoundingClientRect();
        const x = e.clientX - containerRect.left;
        const y = e.clientY - containerRect.top;

        const img = imageRef.current;
        const scaleX = img.naturalWidth / img.clientWidth;
        const scaleY = img.naturalHeight / img.clientHeight;

        const imageX = (x - panOffset.x) * scaleX / zoomLevel;
        const imageY = (y - panOffset.y) * scaleY / zoomLevel;

        const isMouseOverAnyPoint = (x, y) => {
          const threshold = pointRadius / zoomLevel + 2;
          return Object.values(coordinates).some(points =>
            points.some(point => {
              const dx = x - point.x;
              const dy = y - point.y;
              return Math.sqrt(dx * dx + dy * dy) < threshold;
            })
          );
        };

        if (isMouseOverAnyPoint(imageX, imageY)) {
          canvasRef.current.style.cursor = 'move';
        } else {
          canvasRef.current.style.cursor = 'crosshair';
        }
      }
    },
    [
      isDragging,
      draggingPoint,
      isPanning,
      panStart.x,
      panStart.y,
      panOffset.x,
      panOffset.y,
      zoomLevel,
      mouseDownPosition,
      panningThreshold,
      coordinates,
      pointRadius,
    ]
  );

  const handleMouseUp = useCallback(() => {
    setIsPanning(false);
    setIsDragging(false);
    setDraggingPoint(null);
    setMouseDownPosition({ x: null, y: null });
    if (canvasRef.current) {
      canvasRef.current.style.cursor = 'crosshair';
    }
  }, []);

  const handleMouseLeave = () => {
    handleMouseUp();
  };

  const handleCanvasClick = useCallback(
    (e) => {
      if (isDragging || isPanning || hasPanned.current) {
        hasPanned.current = false;
        return;
      }
      if (clickDisabled.current) {
        clickDisabled.current = false;
        return;
      }

      if (!subImage) {
        console.error('SubImageAnnotator: subImage non definito.');
        return;
      }

      const containerRect = imageContainerRef.current.getBoundingClientRect();
      const x = e.clientX - containerRect.left;
      const y = e.clientY - containerRect.top;

      const img = imageRef.current;
      const scaleX = img.naturalWidth / img.clientWidth;
      const scaleY = img.naturalHeight / img.clientHeight;

      const imageX = (x - panOffset.x) * scaleX / zoomLevel;
      const imageY = (y - panOffset.y) * scaleY / zoomLevel;

      setCoordinates((prevCoordinates) => {
        const updatedCoordinates = { ...prevCoordinates };
        const key = currentLine;

        if (updatedCoordinates[key].length < 2) {
          updatedCoordinates[key] = [...updatedCoordinates[key], { x: imageX, y: imageY }];
        }

        return updatedCoordinates;
      });
      hasPanned.current = false;
    },
    [
      isDragging,
      isPanning,
      clickDisabled,
      panOffset.x,
      panOffset.y,
      zoomLevel,
      currentLine,
      subImage,
    ]
  );

  const handleMouseDown = useCallback(
    (e) => {
      hasPanned.current = false;
      setMouseDownPosition({ x: e.clientX, y: e.clientY });

      if (!subImage) {
        console.error('SubImageAnnotator: subImage non definito.');
        return;
      }

      const containerRect = imageContainerRef.current.getBoundingClientRect();
      const x = e.clientX - containerRect.left;
      const y = e.clientY - containerRect.top;

      const img = imageRef.current;
      const scaleX = img.naturalWidth / img.clientWidth;
      const scaleY = img.naturalHeight / img.clientHeight;

      const imageX = (x - panOffset.x) * scaleX / zoomLevel;
      const imageY = (y - panOffset.y) * scaleY / zoomLevel;

      let foundPoint = null;
      let foundKey = null;

      Object.entries(coordinates).forEach(([key, points]) => {
        points.forEach((point, idx) => {
          const dx = imageX - point.x;
          const dy = imageY - point.y;
          const distance = Math.sqrt(dx * dx + dy * dy);
          if (distance < pointRadius / zoomLevel + 2) {
            foundPoint = idx;
            foundKey = key;
          }
        });
      });

      if (foundPoint !== null && foundKey !== null) {
        setIsDragging(true);
        setDraggingPoint({ key: foundKey, pointIndex: foundPoint });
        clickDisabled.current = false;
      } else {
        clickDisabled.current = false;
      }
    },
    [panOffset.x, panOffset.y, zoomLevel, pointRadius, coordinates, subImage]
  );

  const handleWheel = useCallback(
    (e) => {
      e.preventDefault();

      if (!subImage) {
        console.error('SubImageAnnotator: subImage non definito.');
        return;
      }

      const containerRect = imageContainerRef.current.getBoundingClientRect();
      const x = e.clientX - containerRect.left;
      const y = e.clientY - containerRect.top;

      const zoomAmount = e.deltaY * -0.001;
      const newZoomLevel = Math.min(Math.max(zoomLevel + zoomAmount, minZoom), maxZoom);
      const zoomRatio = newZoomLevel / zoomLevel;

      const newPanOffset = {
        x: x - (x - panOffset.x) * zoomRatio,
        y: y - (y - panOffset.y) * zoomRatio,
      };

      setZoomLevel(newZoomLevel);
      setPanOffset(newPanOffset);
    },
    [zoomLevel, panOffset.x, panOffset.y, minZoom, maxZoom, subImage]
  );

  const handleReset = useCallback(() => {
    setCoordinates((prevCoordinates) => ({
      ...prevCoordinates,
      [currentLine]: [],
    }));
    setRatios((prevRatios) => {
      const updatedRatios = { ...prevRatios };
      delete updatedRatios[currentLine];
      return updatedRatios;
    });
  }, [currentLine]);

  const handleResetZoom = useCallback(() => {
    setZoomLevel(1);
    setPanOffset({ x: 0, y: 0 });
  }, []);

  const handleUndoLastPoint = useCallback(() => {
    const key = currentLine;
    setCoordinates((prevCoordinates) => {
      const updatedCoordinates = { ...prevCoordinates };
      if (updatedCoordinates[key].length > 0) {
        updatedCoordinates[key] = updatedCoordinates[key].slice(0, -1);
      }
      return updatedCoordinates;
    });
  }, [currentLine]);

  const calculateLineLength = useCallback((points) => {
    if (points.length === 2) {
      const dx = points[1].x - points[0].x;
      const dy = points[1].y - points[0].y;
      return Math.sqrt(dx * dx + dy * dy);
    }
    return null; // Return null when length is invalid
  }, []);

  const [i3mResult, setI3MResult] = useState(null);

  // Stato locale per sesso
  const [sessoState, setSessoState] = useState(sesso || '');

  const saveCurrentSession = useCallback(() => {
    if (!sessoState) {
      // Non salvare la sessione se i dati non sono completi
      return;
    }

    if (!subImage) {
      console.error('SubImageAnnotator: subImage non definito durante il salvataggio.');
      return;
    }

    const sessionData = {
      subImage: {
        ...subImage,
        name: subImage.name,
      },
      tool,
      coordinates,
      ratios,
      i3mValue,
      email,
      tipo,
      posizione,
      sesso: sessoState,
      realAge, // Add realAge to session data
      timestamp: new Date().toISOString(),
      i3mResult,
      isExcluded, // Includi lo stato di esclusione
    };
    saveCurrentSessionDebounced(sessionData);
  }, [
    coordinates,
    ratios,
    i3mValue,
    subImage,
    email,
    tipo,
    posizione,
    sessoState,
    realAge, // Add realAge to dependencies
    saveCurrentSessionDebounced,
    i3mResult,
    isExcluded,
  ]);

  // Modifica il useEffect per salvare solo dopo il caricamento iniziale
  useEffect(() => {
    if (annotationsLoaded.current) {
      saveCurrentSession();
      // Chiama collectAnnotations solo se ci sono cambiamenti effettivi
      if (Object.keys(coordinates).length > 0 || Object.keys(ratios).length > 0) {
        collectAnnotations(subImageId, { coordinates, ratios }, null);
      }
    }
  }, [coordinates, ratios, i3mValue, saveCurrentSession, collectAnnotations, subImageId]);


  // Caricamento della sessione salvata
  useEffect(() => {
    if (annotationsLoaded.current) {
      return;
    }
    const loadSavedSession = async () => {
      if (!subImage || !subImage.name || !email) {
        console.error('SubImageAnnotator: subImage.name o email mancante.');
        return;
      }

      const savedSession = await getSessionByImageNameAndEmail(
        subImage.name,
        email
      );
      if (savedSession) {
        setCoordinates({
          ...lineKeys.reduce((acc, key) => ({ ...acc, [key]: [] }), {}),
          ...savedSession.coordinates,
        });
        setRatios(savedSession.ratios || {});
        setI3MValue(savedSession.i3mValue || null);
        setI3MResult(savedSession.i3mResult || null);
        setSessoState(savedSession.sesso || sessoState);
        annotationsLoaded.current = true; // Imposta il flag a true dopo il caricamento
      } else if (initialAnnotations) {
        setCoordinates({
          ...lineKeys.reduce((acc, key) => ({ ...acc, [key]: [] }), {}),
          ...initialAnnotations.coordinates,
        });
        setRatios(initialAnnotations.ratios || {});
        setI3MValue(initialAnnotations.i3mValue || null);
        setI3MResult(initialAnnotations.i3mResult || null);
        setSessoState(initialAnnotations.sesso || sessoState);
        annotationsLoaded.current = true; // Imposta il flag a true dopo il caricamento
      }
    };
    loadSavedSession();
  }, [subImage, email, initialAnnotations, sessoState, lineKeys]);

  // Calcolo delle somme totali
  useEffect(() => {
    const updatedRatios = {};
    lineKeys.forEach((lineKey) => {
      const points = coordinates[lineKey];
      if (points.length === 2) {
        const length = calculateLineLength(points);
        // Assicurati che la lunghezza sia un numero valido
        if (length !== null && !isNaN(length) && isFinite(length)) {
          updatedRatios[lineKey] = length;
        }
      }
    });

    // Verifica se i ratios sono cambiati prima di aggiornarli
    let isDifferent = false;
    for (let key in updatedRatios) {
      if (ratios[key] !== updatedRatios[key]) {
        isDifferent = true;
        break;
      }
    }
    if (isDifferent) {
      setRatios((prevRatios) => ({ ...prevRatios, ...updatedRatios }));
    }
  }, [coordinates, lineKeys, calculateLineLength, ratios]);


  useEffect(() => {
    // Calcolo del rapporto (Rosso + Verde) / Blu
    const blueLength = ratios.line1 || 0;
    const redLength = ratios.line2 || 0;
    const greenLength = ratios.line3 || 0;

    console.log(`SubImage ${subImageId} (${subImage?.name}): ratios calcolati:`, { blueLength, redLength, greenLength });

    // Calcola il rapporto solo se abbiamo valori validi
    let newRatio = 0;
    if (blueLength > 0 && (redLength > 0 || greenLength > 0)) {
      newRatio = (redLength + greenLength) / blueLength;
    }

    // Verifica che il rapporto sia un numero valido
    if (isNaN(newRatio) || !isFinite(newRatio)) {
      newRatio = 0;
    }

    console.log(`SubImage ${subImageId} (${subImage?.name}): nuovo rapporto calcolato:`, newRatio);

    if (overallRatio !== newRatio) {
      setOverallRatio(newRatio);
      if (onRatiosChange) {
        console.log(`SubImage ${subImageId} (${subImage?.name}): chiamando onRatiosChange con:`, newRatio);
        onRatiosChange(subImageId, newRatio);
      }
    }
  }, [ratios, overallRatio, onRatiosChange, subImageId, subImage?.name]);

  useEffect(() => {
    const canvas = canvasRef.current;
    const img = imageRef.current;

    if (canvas && img) {
      const ctx = canvas.getContext('2d');
      if (!ctx) return;

      const drawCanvas = () => {
        ctx.clearRect(0, 0, canvas.width, canvas.height);

        Object.entries(coordinates).forEach(([key, points]) => {
          const color = getLineColor(key);

          ctx.fillStyle = color;
          ctx.strokeStyle = color;

          if (points.length > 0) {
            ctx.beginPath();
            ctx.moveTo(points[0].x, points[0].y);
            for (let i = 1; i < points.length; i++) {
              ctx.lineTo(points[i].x, points[i].y);
            }
            ctx.lineWidth = lineThickness;
            ctx.strokeStyle = color;
            ctx.stroke();

            points.forEach((point) => {
              ctx.beginPath();
              ctx.arc(point.x, point.y, pointRadius, 0, 2 * Math.PI);
              ctx.fillStyle = color;
              ctx.fill();
            });
          }
        });
      };

      let animationFrameId = requestAnimationFrame(drawCanvas);

      return () => {
        cancelAnimationFrame(animationFrameId);
      };
    }
  }, [coordinates, lineThickness, pointRadius, getLineColor]);

  useEffect(() => {
    const container = imageContainerRef.current;
    if (!container) return;

    container.addEventListener('wheel', handleWheel, { passive: false });

    return () => {
      container.removeEventListener('wheel', handleWheel);
    };
  }, [handleWheel]);


  const handleSettingClick = (setting) => {
    setActiveSetting(activeSetting === setting ? '' : setting);
  };
  const handleContrastChange = (e) => setContrast(Number(e.target.value));
  const handleBrightnessChange = (e) => setBrightness(Number(e.target.value));
  const handleResetContrast = () => setContrast(100);
  const handleResetBrightness = () => setBrightness(100);
  const handleResetLineThickness = () => setLineThickness(1);
  const handleResetPointRadius = () => setPointRadius(2);


  const handleDownloadAnnotatedImage = useCallback(() => {
    flushSync(() => {
      setZoomLevel(1);
      setPanOffset({ x: 0, y: 0 });
    });

    html2canvas(imageContainerRef.current, { useCORS: true }).then((canvas) => {
      // Salva l'immagine annotata
      canvas.toBlob((blob) => {
        saveAs(blob, subImage.name);
      }, 'image/png', 1);

      // Ottieni il Data URL dell'immagine annotata
      const annotatedImageDataURL = canvas.toDataURL('image/png');

      // Chiama collectAnnotations per passare le annotazioni e l'immagine annotata a ImageSelector
      collectAnnotations(subImageId, { coordinates, ratios }, annotatedImageDataURL);
    });
  }, [subImage.name, collectAnnotations, subImageId, coordinates, ratios]);



  const handleSaveAnnotationsJSON = useCallback(() => {
    if (!subImage) {
      console.error('SubImageAnnotator: subImage non definito durante il salvataggio JSON.');
      return;
    }

    const data = {
      imageName: subImage.name,
      annotations: coordinates,
      ratios: ratios,
      i3mValue: i3mValue,
      email: email,
      tipo: tipo || '',
      posizione: posizione || '',
      gender: sessoState,
      realAge: realAge || '', // Add realAge to JSON data
      timestamp: new Date().toISOString(),
      i3mResult,
      isExcluded: isExcluded, // Includi lo stato di esclusione
    };

    // Chiama collectAnnotations prima di salvare il JSON
    // Questo assicura che le annotazioni siano aggiornate in ImageSelector
    // Se preferisci, puoi chiamarlo altrove
    collectAnnotations(subImageId, { coordinates, ratios }, null); // Passa null se non vuoi aggiornare l'immagine annotata

    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    saveAs(blob, `${subImage.name.replace(/\.[^/.]+$/, '')}_annotations.json`);
  }, [
    subImage,
    coordinates,
    ratios,
    i3mValue,
    email,
    tipo,
    posizione,
    sessoState,
    realAge, // Add realAge to dependencies
    i3mResult,
    isExcluded,
    collectAnnotations,
    subImageId,
  ]);


  const memoizedOnExcludeChange = useCallback((isExcludedFlag) => {
    if (onExcludeChange) {
      onExcludeChange(subImageId, isExcludedFlag); // Passa subImageId
    }
    //eslint-disable-next-line
  }, [onExcludeChange]); // Rimosso subImageId dalle dipendenze

  const [isExcludedState, setIsExcludedState] = useState(isExcluded);

  useEffect(() => {
    setIsExcludedState(isExcluded);
  }, [isExcluded]);

  useEffect(() => {
    memoizedOnExcludeChange(isExcludedState);
  }, [isExcludedState, memoizedOnExcludeChange]);

  // Rimosso il useEffect che chiamava memoizedOnRatiosChange(subImageId, ratios)
  // perché causava una doppia chiamata e il calcolo del rapporto è già gestito
  // nel useEffect del rapporto complessivo sopra

  if (!subImage) {
    // Gestione dell'assenza di subImage
    return (
      <div className="subimage-container">
        <h3 className="subimage-title">Sotto-immagine non disponibile</h3>
      </div>
    );
  }

  if (isExcluded) {
    return (
      <div className="subimage-container">
        <hr></hr>
        <div className="save-buttons-container-top">
          <h3 className="subimage-title">{subImage.name} {' | '}</h3>
          <button onClick={() => setIsExcludedState(false)} className="excluded-button">
            Resume annotation
          </button>

        </div>
        <hr></hr>
      </div>
    );
  }



  return (
    <div className="subimage-container">

      <h3 className="subimage-title">
        {subImage.name}{' | '}
        {subImage.name.includes('lower_second_premolar') ? (
          // Se il nome contiene 'lower second premolar', non visualizzare il pulsante
          null
        ) : (
          <button
            onClick={() => setIsExcludedState(true)}
            className="exclude-button"
            title="Exclude Tooth"
          >
            Closed apices tooth
          </button>
        )}
      </h3>
<div className='tooltips'>
        <div className="tooltip">
          <p>Use 🔵 color to select the height of the tooth</p>
        </div>
        <div className="tooltip">
          <p>Use 🔴 color to select the root of the tooth</p>
        </div>
        <div className="tooltip">
          <p>Use 🟢 color to select the root of the tooth</p>
        </div>
      </div>

      <div className="subimage-topbar">
        <div className="icon-group">
          {lineKeys.map((line) => (
            <button
              key={line}
              onClick={() => handleLineChange(line)}
              className={`topbar-button ${currentLine === line ? 'active-line' : ''}`}
              title={`Color ${lineKeyToColor[line]}`}
              style={{ backgroundColor: getLineColor(line) }}
            >
              <FaMinus className="topbar-icon" />
            </button>
          ))}
        </div>
        <div className="icon-group">
          <button onClick={handleUndoLastPoint} className="topbar-button" title="Undo Last Point">
            <FaUndo className="topbar-icon" />
          </button>
          <button onClick={handleReset} className="topbar-button" title="Reset">
            <FaEraser className="topbar-icon" />
          </button>
        </div>
        <div className="icon-group">
          <button
            onClick={handleResetZoom}
            className="topbar-button"
            title="Reset Zoom"
          >
            <FaSearchMinus className="topbar-icon" />
          </button>
        </div>
        <div className="icon-group">
          <button
            onClick={() => setShowSaveMenu(!showSaveMenu)}
            className="topbar-button"
            title="Save"
          >
            <FaSave className="topbar-icon" />
          </button>
          <button
            onClick={() => setShowSettingsMenu(!showSettingsMenu)}
            className={`topbar-button${showSettingsMenu ? ' active-settings' : ''}`}
            title="Settings"
          >
            <FaCog className="topbar-icon" />
          </button>
        </div>
      </div>

      {showSettingsMenu && (
        <div className="subimage-topbar settings-menu">
          <div className="settings-icons-container">
            <button
              className={`settings-icon-button${activeSetting === 'contrast' ? ' active' : ''}`}
              onClick={() => handleSettingClick('contrast')}
              title="Contrast"
            >
              <FaAdjust className="topbar-icon" />
            </button>
            <button
              className={`settings-icon-button${activeSetting === 'brightness' ? ' active' : ''}`}
              onClick={() => handleSettingClick('brightness')}
              title="Brightness"
            >
              <FaSun className="topbar-icon" />
            </button>
            <button
              className={`settings-icon-button${activeSetting === 'lineThickness' ? ' active' : ''}`}
              onClick={() => handleSettingClick('lineThickness')}
              title="Line Thickness"
            >
              <FaPaintBrush className="topbar-icon" />
            </button>
            <button
              className={`settings-icon-button${activeSetting === 'pointRadius' ? ' active' : ''}`}
              onClick={() => handleSettingClick('pointRadius')}
              title="Point Size"
            >
              <FaCircle className="topbar-icon" />
            </button>
          </div>

          <div className={`slider-container ${activeSetting === 'contrast' ? 'visible' : ''}`}>
            <input
              type="range"
              min="50"
              max="200"
              value={contrast}
              onChange={handleContrastChange}
            />
            <div className="slider-value">
              {contrast}%
              <span className="reset-icon" onClick={handleResetContrast}>🔄</span>
            </div>
          </div>

          <div className={`slider-container ${activeSetting === 'brightness' ? 'visible' : ''}`}>
            <input
              type="range"
              min="50"
              max="200"
              value={brightness}
              onChange={handleBrightnessChange}
            />
            <div className="slider-value">
              {brightness}%
              <span className="reset-icon" onClick={handleResetBrightness}>🔄</span>
            </div>
          </div>

          <div className={`slider-container ${activeSetting === 'lineThickness' ? 'visible' : ''}`}>
            <input
              type="range"
              min="1"
              max="5"
              value={lineThickness}
              onChange={(e) => setLineThickness(parseInt(e.target.value))}
            />
            <div className="slider-value">
              {lineThickness}
              <span className="reset-icon" onClick={handleResetLineThickness}>🔄</span>
            </div>
          </div>

          <div className={`slider-container ${activeSetting === 'pointRadius' ? 'visible' : ''}`}>
            <input
              type="range"
              min="2"
              max="10"
              value={pointRadius}
              onChange={(e) => setPointRadius(parseInt(e.target.value))}
            />
            <div className="slider-value">
              {pointRadius}
              <span className="reset-icon" onClick={handleResetPointRadius}>🔄</span>
            </div>
          </div>
        </div>
      )}

      {showSaveMenu && (
        <div className="subimage-topbar save-menu">
          <div className="save-buttons-container">
            <button
              onClick={handleDownloadAnnotatedImage}
              className="save-button"
              title="Save Image"
            >
              <FaDownload className="button-icon" />
              Save Image
            </button>
            <button
              onClick={handleSaveAnnotationsJSON}
              className="save-button"
              title="Save Annotations (JSON)"
            >
              <FaSave className="button-icon" />
              Save Annotations
            </button>
          </div>
        </div>
      )}

      <div className="annotator-container">
        <div
          className="image-container"
          ref={imageContainerRef}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseLeave}
        >
          <div
            className="image-wrapper"
            style={{
              transform: `translate(${panOffset.x}px, ${panOffset.y}px) scale(${zoomLevel})`,
              transformOrigin: '0 0',
            }}
          >
            <img
              src={subImage.dataURL}
              alt="Immagine selezionata"
              ref={imageRef}
              className="annotator-image"
              crossOrigin="anonymous"
              style={{ filter: `contrast(${contrast}%) brightness(${brightness}%)` }}
              onLoad={() => {
                const img = imageRef.current;
                const canvas = canvasRef.current;
                if (img && canvas) {
                  canvas.width = img.naturalWidth;
                  canvas.height = img.naturalHeight;
                }
              }}
            />
            <canvas
              ref={canvasRef}
              className="annotator-canvas"
              onClick={handleCanvasClick}
              style={{
                width: '100%',
                height: '100%',
              }}
            />
          </div>
        </div>
      </div>
      <div>
        <div className="coordinates-container">

          {lineKeys.map((line) => {
            const color = getLineColor(line);
            return (
              <div key={line} className="coordinate-box">
                <p className="point-text-small point-text-title" style={{ color: color }}>
                  Line ({color})
                </p>
                {coordinates[line] && coordinates[line].map((point, idx) => (
                  <p key={idx} className="point-text-small">
                    {idx + 1}: ({Math.round(point.x)}, {Math.round(point.y)})
                  </p>
                ))}
                {ratios[line] && ratios[line] !== null ? (
                  <p className="point-text-small">Lenght: {Math.round(ratios[line])} px</p>
                ) : (
                  <p className="point-text-small">Lenght: N/A</p>
                )}
              </div>
            );
          })}
        </div>

      </div>
    </div>

  );
}

SubImageAnnotator.propTypes = {
  subImage: PropTypes.object.isRequired,
  subImageId: PropTypes.number.isRequired,
  toothType: PropTypes.string.isRequired, // Aggiungi questa linea
  collectAnnotations: PropTypes.func.isRequired,
  email: PropTypes.string.isRequired,
  tipo: PropTypes.string,
  posizione: PropTypes.string,
  regione: PropTypes.string,
  sesso: PropTypes.string,
  realAge: PropTypes.string, // Add realAge to PropTypes
  selectableColors: PropTypes.arrayOf(PropTypes.string),
  initialAnnotations: PropTypes.object,
  updateSessionsCount: PropTypes.func,
  onExcludeChange: PropTypes.func,
  onRatiosChange: PropTypes.func,
  isExcluded: PropTypes.bool.isRequired,
  userInfo: PropTypes.object,
};

export default SubImageAnnotator;
